import React, { useState, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import LandingPage from './pages/LandingPage'
import Dashboard from './pages/Dashboard'
import AuthModal from './components/AuthModal'
import AdminApp from './admin/AdminApp'
import GoogleTranslate from './components/GoogleTranslate'
import { useAuth } from './context/AuthContext'
import { Loader2 } from 'lucide-react'

function App() {
  const { user, loading, isAuthenticated, logout } = useAuth()
  const [currentPage, setCurrentPage] = useState('landing')
  const [showAuth, setShowAuth] = useState(false)
  const [isAdminRoute, setIsAdminRoute] = useState(false)

  // Check if we're on the admin route
  useEffect(() => {
    const checkRoute = () => {
      const path = window.location.pathname
      setIsAdminRoute(path.startsWith('/admin'))
    }

    checkRoute()
    window.addEventListener('popstate', checkRoute)
    return () => window.removeEventListener('popstate', checkRoute)
  }, [])

  // Update current page based on authentication state
  useEffect(() => {
    if (!loading) {
      if (isAuthenticated) {
        setCurrentPage('dashboard')
      } else {
        setCurrentPage('landing')
      }
    }
  }, [isAuthenticated, loading])

  const navigateTo = (page) => {
    setCurrentPage(page)
  }

  const handleLogin = (userData) => {
    setShowAuth(false)
    setCurrentPage('dashboard')
  }

  const handleLogout = async () => {
    await logout()
    setCurrentPage('landing')
  }

  // Render admin panel if on admin route
  if (isAdminRoute) {
    return <AdminApp />
  }

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {currentPage === 'landing' && !isAuthenticated && (
        <LandingPage
          onShowAuth={() => setShowAuth(true)}
          onNavigate={navigateTo}
        />
      )}

      {currentPage === 'dashboard' && isAuthenticated && user && (
        <Dashboard
          user={user}
          onLogout={handleLogout}
          onNavigate={navigateTo}
        />
      )}

      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onLogin={handleLogin}
        />
      )}

      {/* Global Language Selector - appears on all pages */}
      <GoogleTranslate variant="floating" />
    </div>
  )
}

export default App
